import type { ReactNode } from 'react';
import NotesIcon from '@mui/icons-material/Notes';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import { isStructureFinding, type FindingType } from '@ast-data/shared';
import FindingTypeChip from '../FindingTypeChip';
import { DESCRIPTION_PHRASES } from '@ast-data/shared';
import { bindFormFields } from '../../../components/formFields';
import FormRow from '../../../components/FormRow';
import RecordedAtField from '../../../components/RecordedAtField';
import FormSectionBox from '../../../components/FormSectionBox';
import { usePersonPicker } from '../../../components/personPicker';
import { useEmploymentDay } from '../../users/employment';
import type { DescriptionFormValues } from './descriptionFormValues';
import type { FormFieldBinding } from '../../../components/formFields';
import type { FindingRefSupport } from '../../../components/RichTextArea';

/**
 * Who recorded the description, when, and the description itself.
 *
 * The record's own number is passed in rather than named here: it is the one
 * field of this section a Planum and a Profil do not share.
 *
 * A „baulicher Bestand" closes with a second long text: the Ansprache, the
 * reading of what the description above states — directly under the
 * Beschreibung because that is what it answers, and shorter by default.
 */
export default function IdentitySection({
    binding,
    titlePrefix,
    numberField,
    partField,
    partBar,
    findingRefs,
    findingType
}: {
    binding: FormFieldBinding<DescriptionFormValues>;
    titlePrefix: string;
    /** The Planum-/Profilnummer field, rendered by the owning form. */
    numberField: ReactNode;
    /**
     * The Teil mark field, beside the number: which piece of that Planum/Profil
     * this description covers. Empty on an undivided one, which is most of them.
     */
    partField?: ReactNode;
    /**
     * The other Teile of the same number, as links (see DescriptionPartBar).
     * Under the number rather than beside it: it lists RECORDS, not a value.
     */
    partBar?: ReactNode;
    /**
     * Marks and links the Befunde the description names. A description is
     * written in the same prose a Tagebuch day is — "wird von Bef. 7001
     * geschnitten" — so it gets the same editor. Optional so a form can leave
     * the plain field in place.
     */
    findingRefs?: FindingRefSupport;
    /**
     * The kind of the owning Befund. It decides the chip in this box's heading
     * and whether the Ansprache is asked for below, so the form hands over the
     * ANSWER and not two flags that could disagree. Undefined while the Befund
     * is still loading.
     */
    findingType?: FindingType;
}) {
    // Only who was employed on the Aufnahmedatum (see users/employment).
    const personPicker = usePersonPicker(
        useEmploymentDay(binding.control, 'recordedAt')
    );
    const { person, richText } = bindFormFields(binding);
    const isStructure = isStructureFinding(findingType);
    return (
        <FormSectionBox
            titlePrefix={titlePrefix}
            id="identity"
            titleSlot={<FindingTypeChip findingType={findingType} />}
        >
            <FormRow>
                {numberField}
                {partField}
            </FormRow>
            {partBar}
            <FormRow>
                <RecordedAtField control={binding.control} name="recordedAt" />
                {person('editor', personPicker)}
            </FormRow>
            {richText('description', {
                icon: <NotesIcon />,
                minRows: 9,
                library: DESCRIPTION_PHRASES,
                findingRefs
            })}
            {isStructure &&
                richText('interpretation', {
                    icon: <PsychologyOutlinedIcon />,
                    minRows: 4,
                    findingRefs
                })}
        </FormSectionBox>
    );
}
